import React from "react";
import {
  Scale,
  Layers,
  BookOpen,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Building2,
  Users,
  Wallet,
  Target,
  Zap,
} from "lucide-react";

/**
 * MethodPage — Phase 4 of the Research tab.
 *
 * Three sections of static educational content:
 *   1. Risk-character methodology (published formula)
 *   2. Six-pillar evaluation scorecard
 *   3. Glossary of terms used across the Research module
 *
 * All copy is compliance-strict. No "buy / sell / recommend / pick / you
 * should" appears anywhere in this file — including comments, tooltips,
 * and headings. Buy/Hold/Sell language only ever appears as labels for
 * external analyst stances, fully attributed.
 *
 * The persistent disclaimer footer is rendered by the parent ResearchTab
 * container — every screen inherits it.
 */

/* ─── Reusable section primitives ────────────────────────────────── */

const SectionHeader = ({ icon: Icon, eyebrow, title, children }) => (
  <div className="mb-5">
    <div className="flex items-center gap-2 mb-2">
      <Icon size={16} className="text-arc-gold" />
      <p className="eyebrow">{eyebrow}</p>
    </div>
    <h3 className="headline text-xl mb-2">{title}</h3>
    {children && (
      <p className="text-sm text-arc-muted leading-relaxed max-w-3xl">
        {children}
      </p>
    )}
  </div>
);

const Card = ({ children, className = "" }) => (
  <div className={`surface-xl p-5 sm:p-6 ${className}`}>{children}</div>
);

/* ─── 1. Risk-character methodology ──────────────────────────────── */

const VOL_TIERS = [
  { tier: "Low",    range: "< 25%",     note: "Calm price action; routine market moves." },
  { tier: "Medium", range: "25% – 45%", note: "Typical for most large-cap stocks; meaningful swings." },
  { tier: "High",   range: "> 45%",     note: "Sharp moves common; sentiment-driven names." },
];

const CAP_TIERS = [
  { tier: "Mega",  range: "> $200B",     note: "Index heavyweights, high liquidity." },
  { tier: "Large", range: "$10B – $200B", note: "Most established public companies." },
  { tier: "Mid",   range: "$2B – $10B",   note: "Growing names; mixed coverage." },
  { tier: "Small", range: "< $2B",        note: "Higher liquidity risk, wider spreads." },
];

const BS_TIERS = [
  { tier: "Strong",    range: "D/E < 0.5",        note: "Light leverage, room to weather cycles." },
  { tier: "Moderate",  range: "D/E 0.5 – 1.5",    note: "Typical capital structure for most companies." },
  { tier: "Stretched", range: "D/E > 1.5",        note: "High leverage; sensitive to rates and downturns." },
];

const RiskMethodology = () => (
  <Card>
    <SectionHeader
      icon={Scale}
      eyebrow="ARCRYPTO METHODOLOGY"
      title="How we bucket stocks into Conservative, Moderate, and Aggressive"
    >
      Risk character is ARCrypto's own objective bucket — it tells you how
      much price turbulence a stock has historically delivered relative to
      its size and balance sheet. It is <strong>not</strong> a quality
      judgment, <strong>not</strong> a prediction, and <strong>not</strong>
      the analyst's stance.
    </SectionHeader>

    <div className="space-y-5">
      {/* Inputs */}
      <div>
        <h4 className="font-bold text-arc-ink text-sm mb-3">
          Three objective inputs
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Volatility */}
          <div className="rounded-xl border border-arc-line bg-arc-surface p-4">
            <p className="eyebrow mb-2">Input 1 · Volatility</p>
            <p className="text-xs text-arc-muted leading-relaxed mb-3">
              Annualized standard deviation of the last 30 trading days of
              returns. Higher = more turbulent.
            </p>
            <table className="w-full text-[11px]">
              <tbody>
                {VOL_TIERS.map((t) => (
                  <tr key={t.tier} className="border-t border-arc-line">
                    <td className="py-1.5 pr-2 font-semibold text-arc-ink">
                      {t.tier}
                    </td>
                    <td className="py-1.5 num text-arc-gold">{t.range}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Market cap */}
          <div className="rounded-xl border border-arc-line bg-arc-surface p-4">
            <p className="eyebrow mb-2">Input 2 · Market-cap tier</p>
            <p className="text-xs text-arc-muted leading-relaxed mb-3">
              Total market capitalization. Larger caps tend to have deeper
              liquidity and more analyst coverage.
            </p>
            <table className="w-full text-[11px]">
              <tbody>
                {CAP_TIERS.map((t) => (
                  <tr key={t.tier} className="border-t border-arc-line">
                    <td className="py-1.5 pr-2 font-semibold text-arc-ink">
                      {t.tier}
                    </td>
                    <td className="py-1.5 num text-arc-gold">{t.range}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Balance sheet */}
          <div className="rounded-xl border border-arc-line bg-arc-surface p-4">
            <p className="eyebrow mb-2">Input 3 · Balance-sheet stability</p>
            <p className="text-xs text-arc-muted leading-relaxed mb-3">
              Debt-to-equity ratio. Lower means the company is less reliant
              on borrowed money.
            </p>
            <table className="w-full text-[11px]">
              <tbody>
                {BS_TIERS.map((t) => (
                  <tr key={t.tier} className="border-t border-arc-line">
                    <td className="py-1.5 pr-2 font-semibold text-arc-ink">
                      {t.tier}
                    </td>
                    <td className="py-1.5 num text-arc-gold">{t.range}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bucket rules */}
      <div>
        <h4 className="font-bold text-arc-ink text-sm mb-3">
          How the bucket is assigned
        </h4>

        <div className="space-y-2.5">
          <div className="flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
            <div>
              <p className="font-bold text-emerald-300 text-sm mb-0.5">
                Conservative
              </p>
              <p className="text-xs text-arc-muted leading-relaxed">
                Low volatility <strong>and</strong> Mega- or Large-cap{" "}
                <strong>and</strong> Strong or Moderate balance sheet.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/5 p-3.5">
            <span className="w-2 h-2 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />
            <div>
              <p className="font-bold text-red-300 text-sm mb-0.5">
                Aggressive
              </p>
              <p className="text-xs text-arc-muted leading-relaxed">
                High volatility, <strong>or</strong> Small- to Mid-cap with a
                Stretched balance sheet, <strong>or</strong> any company
                with leverage materially above sector norms.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3.5">
            <span className="w-2 h-2 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
            <div>
              <p className="font-bold text-amber-300 text-sm mb-0.5">
                Moderate
              </p>
              <p className="text-xs text-arc-muted leading-relaxed">
                Anything that doesn't fit Conservative or Aggressive —
                medium volatility and a reasonable mix of size and leverage.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Refresh + limits */}
      <div className="rounded-xl border border-arc-line bg-arc-surface/40 p-3.5">
        <p className="text-xs text-arc-dim leading-relaxed">
          <span className="font-semibold text-arc-muted">Refresh cadence: </span>
          buckets are recomputed whenever the underlying market data is
          refreshed by our pipeline. Inputs reflect public market data and
          may lag intraday moves.
        </p>
      </div>
    </div>
  </Card>
);

/* ─── 2. Six-pillar evaluation scorecard ─────────────────────────── */

const PILLARS = [
  {
    icon: Building2,
    name: "Business quality / moat",
    questions: [
      "What does this company actually do, in one sentence?",
      "Why does the customer come back? Switching costs, network effects, brand, scale, IP?",
      "What is the worst competitor's product like, and why is this one durable?",
    ],
    green: "Customers stick around for years without much marketing. Unit economics improve at scale.",
    red:   "Heavy promotional spending to retain users. Substitutes are easy and cheap.",
  },
  {
    icon: Users,
    name: "Management",
    questions: [
      "How long has the leadership team been in place?",
      "Are management's incentives aligned with long-term shareholders (insider ownership, sensible comp)?",
      "Has the team navigated a downturn before?",
    ],
    green: "Founder-led or long-tenured operators with significant skin in the game and a record of capital allocation.",
    red:   "Frequent C-suite turnover, large stock-based compensation dilution, or recent SEC-flagged disclosures.",
  },
  {
    icon: Wallet,
    name: "Financial health",
    questions: [
      "Can the company service its debt across the cycle?",
      "Is free cash flow positive, growing, and well-converted from earnings?",
      "What does the balance sheet look like in a 30% revenue decline?",
    ],
    green: "Net cash, growing free cash flow, modest leverage, durable margins.",
    red:   "Negative free cash flow with no clear path, heavy refinancing risk, or off-balance-sheet liabilities.",
  },
  {
    icon: TrendingUp,
    name: "Growth",
    questions: [
      "What is the actual end-market growing at?",
      "Is the company taking share, or just riding the market?",
      "Is growth profitable (gross margin holding or improving)?",
    ],
    green: "Multi-year revenue and free-cash-flow growth, expanding margins, large addressable market.",
    red:   "Top-line growth funded entirely by losses, declining unit economics, market saturating.",
  },
  {
    icon: Target,
    name: "Valuation / margin of safety",
    questions: [
      "How does the current valuation compare to history and to peers?",
      "What growth rate is the market pricing in, and is it achievable?",
      "What return would I earn if growth slowed materially?",
    ],
    green: "Reasonable multiple relative to growth and quality; multiple expansion is not required for a return.",
    red:   "Multiple at or above all-time highs, justified only by optimistic projections.",
  },
  {
    icon: Zap,
    name: "Catalyst",
    questions: [
      "What event might cause the market to re-rate this name in the next 6–24 months?",
      "Is the catalyst within the company's control, or dependent on external conditions?",
      "What happens if the catalyst doesn't arrive on time?",
    ],
    green: "Identifiable upcoming events (product launches, contract awards, restructurings) with clear measurable signal.",
    red:   "No identifiable catalyst other than 'the market will figure it out'.",
  },
];

const Scorecard = () => (
  <Card>
    <SectionHeader
      icon={Layers}
      eyebrow="EVALUATION FRAMEWORK"
      title="The six-pillar scorecard ARCrypto teaches"
    >
      A teaching framework, not a recommendation engine. When you work through
      a stock — any stock — these are the six questions worth answering before
      you form an opinion. Stronger answers across more pillars do not
      guarantee outcomes; they reduce surprise.
    </SectionHeader>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {PILLARS.map((p) => {
        const Icon = p.icon;
        return (
          <div
            key={p.name}
            className="rounded-xl border border-arc-line bg-arc-surface p-4 space-y-3"
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-arc-gold/10 border border-arc-gold/30 flex items-center justify-center flex-shrink-0">
                <Icon size={14} className="text-arc-gold" />
              </div>
              <h4 className="font-bold text-arc-ink text-sm">{p.name}</h4>
            </div>

            <div>
              <p className="eyebrow mb-1.5">Questions to answer</p>
              <ul className="space-y-1">
                {p.questions.map((q, i) => (
                  <li
                    key={i}
                    className="text-xs text-arc-muted leading-relaxed pl-3 relative before:content-['•'] before:absolute before:left-0 before:text-arc-gold"
                  >
                    {q}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-2.5">
              <CheckCircle2
                size={14}
                className="text-emerald-400 flex-shrink-0 mt-0.5"
              />
              <p className="text-[11px] text-arc-muted leading-relaxed">
                <span className="font-semibold text-emerald-300">
                  Strong signal:{" "}
                </span>
                {p.green}
              </p>
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/5 p-2.5">
              <AlertCircle
                size={14}
                className="text-red-400 flex-shrink-0 mt-0.5"
              />
              <p className="text-[11px] text-arc-muted leading-relaxed">
                <span className="font-semibold text-red-300">Red flag: </span>
                {p.red}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  </Card>
);

/* ─── 3. Glossary ────────────────────────────────────────────────── */

const GLOSSARY = [
  {
    term: "Risk Character",
    def:
      "ARCrypto's objective classification of a stock into Conservative, " +
      "Moderate, or Aggressive — derived from volatility, market-cap tier, " +
      "and balance-sheet leverage. It is not a quality judgment, not a " +
      "prediction, and not the analyst's stance.",
  },
  {
    term: "Analyst Stance · Buy / Hold / Sell",
    def:
      "The published view of a third-party Wall Street analyst on whether " +
      "they expect the stock to outperform (Buy), trade in line with the " +
      "market (Hold), or underperform (Sell) over a roughly 12-month " +
      "window. Always attributed to the named analyst and firm.",
  },
  {
    term: "Success Rate",
    def:
      "Vendor-reported percentage of a given analyst's recent calls that " +
      "closed above their stated target. Methodologies, lookback windows, " +
      "and survivor-bias treatment vary by vendor. Past results do not " +
      "predict future calls.",
  },
  {
    term: "Average Return",
    def:
      "Vendor-reported mean return on this analyst's recommended positions " +
      "over the past 12 months. Past performance is not a prediction of " +
      "future returns and does not include fees, taxes, or slippage.",
  },
  {
    term: "Price Target",
    def:
      "The published price the analyst expects the stock to reach within " +
      "their stated window. Targets are subject to revision and do not " +
      "guarantee that the price will reach the target.",
  },
  {
    term: "Market-cap tier",
    def:
      "Bucket assigned by total market capitalization: Mega (> $200B), " +
      "Large ($10B–$200B), Mid ($2B–$10B), Small (< $2B).",
  },
  {
    term: "Annualized volatility (30-day)",
    def:
      "The standard deviation of daily returns over the last 30 trading " +
      "days, scaled to one year. Higher numbers mean more historical " +
      "price turbulence.",
  },
  {
    term: "Debt-to-equity (D/E)",
    def:
      "Total debt divided by shareholder equity. A measure of how much a " +
      "company relies on borrowed money to finance its operations.",
  },
  {
    term: "Free cash flow",
    def:
      "Cash a company generates from operations after capital expenditures. " +
      "Used by investors to assess how much real cash the business " +
      "produces, independent of accounting earnings.",
  },
  {
    term: "Catalyst",
    def:
      "An identifiable upcoming event (earnings release, regulatory " +
      "decision, product launch, contract award, etc.) that may cause the " +
      "market's view of a stock to change. Catalysts are not guarantees " +
      "and may resolve in either direction.",
  },
];

const Glossary = () => (
  <Card>
    <SectionHeader
      icon={BookOpen}
      eyebrow="GLOSSARY"
      title="Every term used on the Analyst Ratings cards"
    >
      Plain-language definitions for each metric and label you'll encounter
      in the Research tab. Use as a reference while reading the cards.
    </SectionHeader>

    <dl className="space-y-3">
      {GLOSSARY.map((g) => (
        <div
          key={g.term}
          className="rounded-xl border border-arc-line bg-arc-surface p-4"
        >
          <dt className="font-bold text-arc-ink text-sm mb-1">{g.term}</dt>
          <dd className="text-xs text-arc-muted leading-relaxed">{g.def}</dd>
        </div>
      ))}
    </dl>
  </Card>
);

/* ─── Page ───────────────────────────────────────────────────────── */

const MethodPage = () => (
  <div className="space-y-6">
    <RiskMethodology />
    <Scorecard />
    <Glossary />
  </div>
);

export default MethodPage;
